import { getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue';

const json = value => JSON.stringify(value);
// Editors hold the draft as Vue reactive proxies; structuredClone (graph model,
// history diffs) cannot copy those, so committed data is plain JSON.
const plain = value => JSON.parse(JSON.stringify(value ?? null));

/**
 * Working copy of a Home Assistant flow node for the HASSBuilder editors,
 * which mutate an AST unit in place (bindings, conditions, actions), with
 * every edit committed to the graph.
 *
 * - `build()` makes a draft from the node; `snapshot(draft)` turns it into
 *   the node's data form; `current()` is the node's data in that form.
 * - An edit commits `snapshot(draft)` (as plain JSON) when it differs from
 *   `current()`.
 * - The draft is rebuilt when `current()` changes to something the draft
 *   does not hold (undo/redo, another editor, a wire) or when `rebuildOn()`
 *   changes — never after its own commit, so state editors keep on the
 *   draft survives typing.
 * - Neither a rebuild nor what editors normalize while mounting is
 *   committed: that would push a history entry and drop the redo stack.
 *
 * @returns {import('vue').Ref} the draft
 */
export function useNodeDraft({ build, snapshot, current, commit, rebuildOn = () => null }) {
    const draft = ref(build());
    let syncing = true;
    const settle = () => nextTick(() => { syncing = false; });
    if (getCurrentInstance()) onMounted(settle);
    else settle();

    const rebuild = () => {
        syncing = true;
        draft.value = build();
        settle();
    };
    watch(() => json(current()), value => {
        if (value !== json(plain(snapshot(draft.value)))) rebuild();
    });
    watch(() => json(rebuildOn()), rebuild);
    watch(draft, value => {
        if (syncing) return;
        const data = plain(snapshot(value));
        if (json(data) !== json(current())) commit(data);
    }, { deep: true });
    return draft;
}
